import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Joyride from 'react-joyride';
import { useNavigate } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';

import DottedTexture from '@/assets/branding/dotted-texture.svg?react';
import { Dialog, DialogContent } from '@/components/ui';
import { Button } from '@/components/ui/button';
import { TourTooltip, MobileTourTooltip } from '@/components/ui/tour-tooltip';
import { ROUTES } from '@/config/routes';
import { fetchAccount } from '@/features/account/api/get-account';
import {
  getCapitalDemoAccount,
  getCapitalTourSteps,
} from '@/features/vest-capital/utils/tour';
import useFundedAccount from '@/hooks/use-funded-account';
import useViewport from '@/hooks/use-viewport';
import { useModalStore } from '@/store/use-modal-store';
import { AccountResponse } from '@/types';
import { handleAccountForStore } from '@/utils/handle-store-data';

const CapitalTourModal = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isEval } = useFundedAccount();
  const isMobile = useViewport() === 'sm';
  const steps = getCapitalTourSteps(t);

  const { isStartOpen, isEndOpen } = useModalStore(
    useShallow((state) => ({
      isStartOpen: state.isCapitalTourStartModalOpen,
      isEndOpen: state.isCapitalTourEndModalOpen,
    })),
  );

  const accountSnapshot = useRef<AccountResponse>();
  const [step, setStep] = useState<number>(0);
  const [mobileStep, setMobileStep] = useState<number>(-1);
  const [run, setRun] = useState<boolean>(false);
  const [isNavigating, setIsNavigating] = useState<boolean>(false);
  const isOpen =
    isEval && (isStartOpen || isEndOpen || (isMobile && mobileStep > -1));
  const isRunning = isOpen || run;

  useEffect(() => {
    useModalStore.setState({ isCapitalTourOpen: isRunning });

    if (isRunning) {
      fetchAccount()
        .then((accountResponse) => {
          accountSnapshot.current = accountResponse;
          handleAccountForStore(getCapitalDemoAccount(accountResponse));
        })
        .catch((error) => {
          console.error('Failed to fetch account for demo tour:', error);
        });
    } else if (accountSnapshot.current) {
      handleAccountForStore({
        ...accountSnapshot.current,
        time: accountSnapshot.current.time + 2,
      });
    }
  }, [isRunning]);

  useEffect(() => {
    if (!isRunning) {
      return;
    }

    const handlePopState = () => {
      useModalStore.setState({
        isCapitalTourStartModalOpen: true,
        isCapitalTourEndModalOpen: false,
      });
      setRun(false);
      setStep(0);
      setMobileStep(-1);
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isRunning]);

  const startCapitalTour = () => {
    if (isMobile) {
      setMobileStep(0);
      useModalStore.setState({ isCapitalTourStartModalOpen: false });
    } else {
      navigate(`${ROUTES.PORTFOLIO}?view=positions`);
      setIsNavigating(true);

      setTimeout(() => {
        setRun(true);
        useModalStore.setState({ isCapitalTourStartModalOpen: false });
        setIsNavigating(false);
      }, 500);
    }
  };

  const endCapitalTour = () => {
    useModalStore.setState({
      isCapitalTourStartModalOpen: false,
      isCapitalTourEndModalOpen: false,
    });
    setMobileStep(-1);
    setRun(false);
    navigate(ROUTES.TRADE);
  };

  const handleMobileBack = () => {
    if (mobileStep > 0) {
      setMobileStep(mobileStep - 1);
    }
  };

  const handleMobileNext = () => {
    if (mobileStep < steps.length - 1) {
      setMobileStep(mobileStep + 1);
    } else {
      useModalStore.setState({
        isCapitalTourStartModalOpen: false,
        isCapitalTourEndModalOpen: true,
      });
      setMobileStep(-1);
    }
  };

  const handleJoyrideCallback = (data: any) => {
    const { action, index, status, type } = data;

    if (status === 'finished' || status === 'skipped') {
      useModalStore.setState({ isCapitalTourEndModalOpen: true });
      setRun(false);
    }

    if (type === 'step:after') {
      if (action === 'prev') {
        setStep(index - 1);
      } else if (action === 'next') {
        setStep(index + 1);
      }
    }
  };

  return (
    <>
      {!isMobile && (
        <Joyride
          run={run}
          stepIndex={step}
          steps={steps}
          continuous
          showProgress
          disableOverlayClose
          disableCloseOnEsc
          spotlightPadding={0}
          floaterProps={{
            offset: 0,
            disableFlip: true,
            disableAnimation: true,
            options: { offset: { offset: 0 } },
          }}
          styles={{
            spotlight: {
              borderRadius: '1.5rem',
            },
            options: {
              overlayColor: 'rgba(0, 0, 0, 0.8)',
            },
          }}
          tooltipComponent={TourTooltip}
          callback={handleJoyrideCallback}
        />
      )}

      <Dialog open={isOpen}>
        <DialogContent
          hideCloseButton
          className="gap-10 overflow-hidden border-none sm:max-w-[400px] sm:!rounded-3xl"
        >
          {isMobile && mobileStep > -1 ? (
            <MobileTourTooltip
              step={steps[mobileStep]}
              stepIndex={mobileStep}
              stepsCount={steps.length}
              onBack={handleMobileBack}
              onNext={handleMobileNext}
            />
          ) : (
            <>
              <div className="relative flex flex-col gap-5 p-5 pb-0 pt-8 sm:p-8 sm:pb-0">
                <DottedTexture className="pointer-events-none absolute top-0 z-[-1] h-auto w-full scale-[1.7]" />

                <div className="text-center text-4xl font-medium capitalize">
                  {isStartOpen ? (
                    <>
                      {t('vestCapital.tour.welcome')}
                      <br />
                      {t('vestCapital.tour.fundedAccount')}
                    </>
                  ) : (
                    t('vestCapital.tour.youAreReady')
                  )}
                </div>

                <div className="text-center text-xl text-muted-foreground">
                  {isStartOpen
                    ? t('vestCapital.tour.proveYourSkills')
                    : t('vestCapital.tour.startYourChallenge')}
                </div>
              </div>

              <div className="flex flex-col gap-3 p-5 pt-0 sm:p-8 sm:pt-0">
                {isStartOpen ? (
                  <Button
                    variant="contrast"
                    size="lg"
                    disabled={isNavigating}
                    onClick={startCapitalTour}
                  >
                    {t('vestCapital.tour.showMeAround')}
                  </Button>
                ) : (
                  <Button variant="contrast" size="lg" onClick={endCapitalTour}>
                    {t('vestCapital.tour.startTrading')}
                  </Button>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CapitalTourModal;
